import { useEffect, useMemo, useState } from "react";
import { formatEther } from "viem";
import { winSound } from "../lib/sound";
import { buildWinCardDataUrl, shareWinToX, winText, type WinCard } from "../lib/shareCard";
import { getXToken, postToX } from "../lib/xAuth";

type Props = {
  payout: bigint;
  multiplier: string;
  totalBet: bigint;
  /** X handle (no @) when the winner is logged into X — enables the share card. */
  handle?: string;
  avatarUrl?: string;
  onDone: () => void;
};

const ARROW_COUNT = 22;

/** A Robin Hood archery arrow: head, shaft, feather fletching, nock. */
function Arrow() {
  return (
    <svg viewBox="0 0 120 22" aria-hidden>
      <rect x="12" y="9.6" width="86" height="2.8" rx="1.4" fill="#d4af37" />
      <path d="M119 11 L96 4.5 L101.5 11 L96 17.5 Z" fill="#e8e6df" stroke="#9a978c" strokeWidth="0.6" />
      <path d="M14 10.4 L2 2.5 L8.5 11 L2 19.5 L14 11.6 Z" fill="#00c805" />
      <path d="M22 10.4 L11 3.5 L17 11 L11 18.5 L22 11.6 Z" fill="#29ff5b" opacity="0.9" />
      <rect x="0" y="9" width="4" height="4" rx="1" fill="#8a6a1c" />
    </svg>
  );
}

export function WinCelebration({ payout, multiplier, totalBet, handle, avatarUrl, onDone }: Props) {
  const [sharing, setSharing] = useState(false);
  const [postedUrl, setPostedUrl] = useState<string | null>(null);

  useEffect(() => {
    winSound();
    const encore = setTimeout(() => winSound(), 450);
    const t = setTimeout(onDone, 9000);
    return () => {
      clearTimeout(t);
      clearTimeout(encore);
    };
  }, [onDone]);

  const arrows = useMemo(
    () =>
      Array.from({ length: ARROW_COUNT }, () => {
        const fromLeft = Math.random() < 0.5;
        const txVw = fromLeft ? 170 : -170;
        const tyVh = -45 + Math.random() * 90;
        const rot = (Math.atan2(tyVh, txVw) * 180) / Math.PI;
        return {
          top: 4 + Math.random() * 88,
          left: fromLeft ? -14 : 104,
          width: 80 + Math.random() * 70,
          dur: 1 + Math.random() * 1.3,
          delay: Math.random() * 3.6,
          txVw,
          tyVh,
          rot,
        };
      }),
    []
  );

  const card: WinCard = {
    amountEth: `+${formatEther(payout)}`,
    multiplier,
    totalEth: formatEther(totalBet),
    handle,
    avatarUrl,
  };

  const share = async () => {
    setSharing(true);
    const token = getXToken();
    // Signed in with write scope → post the card straight to X, image attached.
    if (token) {
      try {
        const dataUrl = await buildWinCardDataUrl(card);
        const url = await postToX(winText(card), dataUrl);
        setPostedUrl(url);
        setSharing(false);
        return;
      } catch {
        // fall through to the manual compose+download path
      }
    }
    try {
      await shareWinToX(card);
    } catch {
      /* cancelled / unsupported */
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="win-overlay" role="alert" onClick={onDone}>
      {arrows.map((a, i) => (
        <span
          key={i}
          className="win-arrow"
          style={
            {
              top: `${a.top}%`,
              left: `${a.left}%`,
              width: a.width,
              "--dur": `${a.dur}s`,
              "--delay": `${a.delay}s`,
              "--tx": `${a.txVw}vw`,
              "--ty": `${a.tyVh}vh`,
              "--rot": `${a.rot.toFixed(1)}deg`,
            } as React.CSSProperties
          }
        >
          <Arrow />
        </span>
      ))}

      <div className="win-banner" onClick={(e) => e.stopPropagation()}>
        <span className="win-congrats">CONGRATULATIONS</span>
        <span className="win-amount">YOU WON {formatEther(payout)} ETH</span>
        <div className="win-actions">
          {postedUrl ? (
            <a className="win-share" href={postedUrl} target="_blank" rel="noreferrer">
              𝕏 POSTED — VIEW ON X
            </a>
          ) : (
            handle && (
              <button className="win-share" onClick={() => void share()} disabled={sharing}>
                {sharing ? "POSTING…" : "𝕏 SHARE YOUR WIN"}
              </button>
            )
          )}
          <button className="win-close" onClick={onDone}>
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
}
